// qs-store-layers.ts
import type { DrawingPath, Layer } from '@/types/qs-types-index'
import { useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed } from 'vue'

export const useLayerStore = defineStore('layers', () => {
  const layers = useStorage<Layer[]>('vectorsketch-layers', [])
  const currentLayerId = useStorage<string | null>('vectorsketch-current-layer', null)
  const layerCounter = useStorage('vectorsketch-layer-counter', 0)

  if (layers.value.length === 0) {
    const defaultLayer: Layer = {
      id: 'layer-1',
      name: 'L1',
      visible: true,
      locked: false,
      opacity: 1,
      order: 1,
      paths: [],
      position: { x: 0, y: 0 },
      createdAt: new Date(),
      updatedAt: new Date()
    }
    layers.value = [defaultLayer]
    currentLayerId.value = defaultLayer.id
    layerCounter.value = 1
  }

  const generateUniqueLayerId = () => {
    const timestamp = Date.now()
    const random = Math.random().toString(36).substring(2, 11)
    return `layer-${timestamp}-${random}`
  }

  const generateUniqueLayerName = () => {
    const existingLayers = layers.value.filter(layer => layer.name.match(/^L\d+$/))
    const number = existingLayers.length + 1
    return `L${number}`
  }

  const generateDuplicateLayerName = (originalName: string) => {
    const duplicateName = `${originalName}D`
    let finalName = duplicateName
    let counter = 1
    while (layers.value.some(layer => layer.name === finalName)) {
      finalName = `${originalName}${'D'.repeat(counter + 1)}`
      counter++
    }
    return finalName
  }

  const getNextOrderValue = () => {
    if (layers.value.length === 0) return 1
    const maxOrder = Math.max(...layers.value.map(layer => layer.order))
    return maxOrder + 1
  }

  const validateLayerUniqueness = () => {
    const ids = layers.value.map(l => l.id)
    const names = layers.value.map(l => l.name)
    const uniqueIds = new Set(ids)
    const uniqueNames = new Set(names)
    return ids.length === uniqueIds.size && names.length === uniqueNames.size
  }

  const currentLayer = computed(() =>
    layers.value.find(layer => layer.id === currentLayerId.value)
  )

  const visibleLayers = computed(() =>
    layers.value.filter(layer => layer.visible)
  )

  const layersInOrder = computed(() =>
    [...layers.value].sort((a, b) => b.order - a.order)
  )

  const createLayer = (name?: string): Layer => {
    const layerId = generateUniqueLayerId()
    const layerName = name || generateUniqueLayerName()

    let finalName = layerName
    if (name && name.match(/^L\d+$/)) {
      if (layers.value.some(layer => layer.name === name)) {
        finalName = generateUniqueLayerName()
      }
    }

    const newLayer: Layer = {
      id: layerId,
      name: finalName,
      visible: true,
      locked: false,
      opacity: 1,
      order: getNextOrderValue(),
      paths: [],
      position: { x: 0, y: 0 },
      createdAt: new Date(),
      updatedAt: new Date()
    }

    layers.value = [...layers.value, newLayer]
    currentLayerId.value = newLayer.id
    validateLayerUniqueness()

    return newLayer
  }

  const deleteLayer = (layerId: string) => {
    const layerToDelete = layers.value.find(layer => layer.id === layerId)
    if (!layerToDelete) return

    const newLayers = layers.value.filter(layer => layer.id !== layerId)
    const sortedLayers = [...newLayers].sort((a, b) => b.order - a.order)
    sortedLayers.forEach((layer, idx) => {
      layer.order = sortedLayers.length - idx
    })

    layers.value = sortedLayers

    if (currentLayerId.value === layerId) {
      currentLayerId.value = layers.value[0]?.id ?? null
    }

    validateLayerUniqueness()
  }

  const setCurrentLayer = (layerId: string): boolean => {
    const layer = layers.value.find(l => l.id === layerId)
    if (!layer || layer.locked) {
      return false
    }
    currentLayerId.value = layerId
    return true
  }

  const toggleLayerVisibility = (layerId: string) => {
    const layer = layers.value.find(l => l.id === layerId)
    if (layer) {
      layer.visible = !layer.visible
      layer.updatedAt = new Date()
      layers.value = [...layers.value]
    }
  }

  const toggleLayerLock = (layerId: string) => {
    const layer = layers.value.find(l => l.id === layerId)
    if (layer) {
      layer.locked = !layer.locked
      layer.updatedAt = new Date()
      layers.value = [...layers.value]
    }
  }

  const setLayerOpacity = (layerId: string, opacity: number) => {
    const layer = layers.value.find(l => l.id === layerId)
    if (layer) {
      layer.opacity = Math.max(0, Math.min(1, opacity))
      layer.updatedAt = new Date()
      layers.value = [...layers.value]
    }
  }

  const renameLayer = (layerId: string, newName: string): boolean => {
    const layer = layers.value.find(l => l.id === layerId)
    if (!layer) return false

    const existingLayer = layers.value.find(l => l.id !== layerId && l.name === newName)
    if (existingLayer) {
      return false
    }

    layer.name = newName
    layer.updatedAt = new Date()
    layers.value = [...layers.value]

    return true
  }

  const reorderLayers = (fromIndex: number, toIndex: number): boolean => {
    if (layers.value.length === 0) {
      return false
    }

    const sortedLayers = [...layers.value].sort((a, b) => b.order - a.order)

    if (fromIndex < 0 || fromIndex >= sortedLayers.length || toIndex < 0 || toIndex >= sortedLayers.length) {
      return false
    }

    if (fromIndex === toIndex) {
      return true
    }

    const layerToMove = sortedLayers[fromIndex]
    if (!layerToMove) {
      return false
    }

    try {
      sortedLayers.splice(fromIndex, 1)
      sortedLayers.splice(toIndex, 0, layerToMove)

      sortedLayers.forEach((layer, index) => {
        layer.order = sortedLayers.length - index
      })

      layers.value = sortedLayers
      return true
    } catch {
      return false
    }
  }

  const addPathToLayer = (layerId: string, path: DrawingPath) => {
    const layer = layers.value.find(l => l.id === layerId)
    if (layer) {
      layer.paths.push(path)
      layer.updatedAt = new Date()
      layers.value = [...layers.value]

      window.dispatchEvent(new CustomEvent('layer-updated', {
        detail: { layerId, action: 'path-added' }
      }))
    }
  }

  const removePathFromLayer = (layerId: string, pathId: string) => {
    const layer = layers.value.find(l => l.id === layerId)
    if (layer) {
      layer.paths = layer.paths.filter(p => p.id !== pathId)
      layer.updatedAt = new Date()
      layers.value = [...layers.value]

      window.dispatchEvent(new CustomEvent('layer-updated', {
        detail: { layerId, action: 'path-removed' }
      }))
    }
  }

  const clearLayer = (layerId: string) => {
    const layer = layers.value.find(l => l.id === layerId)
    if (layer) {
      layer.paths = []
      layer.updatedAt = new Date()
      layers.value = [...layers.value]

      window.dispatchEvent(new CustomEvent('layer-updated', {
        detail: { layerId, action: 'layer-cleared' }
      }))
    }
  }

  const duplicateLayer = (layerId: string): Layer | null => {
    const layer = layers.value.find(l => l.id === layerId)
    if (!layer) return null

    const newId = generateUniqueLayerId()
    const newName = generateDuplicateLayerName(layer.name)

    const duplicatedLayer: Layer = {
      ...layer,
      id: newId,
      name: newName,
      order: getNextOrderValue(),
      createdAt: new Date(),
      updatedAt: new Date(),
      paths: layer.paths.map(path => ({
        ...path,
        id: `path-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
        layerId: newId
      }))
    }

    layers.value = [...layers.value, duplicatedLayer]
    currentLayerId.value = newId
    validateLayerUniqueness()

    return duplicatedLayer
  }

  const initializeDefaultLayers = () => {
    layers.value = []
    layerCounter.value = 0

    const defaultLayer: Layer = {
      id: generateUniqueLayerId(),
      name: 'L1',
      visible: true,
      locked: false,
      opacity: 1,
      order: 1,
      paths: [],
      position: { x: 0, y: 0 },
      createdAt: new Date(),
      updatedAt: new Date()
    }

    layers.value = [defaultLayer]
    currentLayerId.value = defaultLayer.id
  }

  const migrateLayers = () => {
    layers.value.forEach(layer => {
      if (!layer.position) {
        layer.position = { x: 0, y: 0 }
      }
    })
  }

  migrateLayers()

  return {
    layers,
    currentLayerId,
    layerCounter,
    currentLayer,
    visibleLayers,
    layersInOrder,
    createLayer,
    deleteLayer,
    setCurrentLayer,
    toggleLayerVisibility,
    toggleLayerLock,
    setLayerOpacity,
    renameLayer,
    reorderLayers,
    addPathToLayer,
    removePathFromLayer,
    clearLayer,
    duplicateLayer,
    initializeDefaultLayers,
    migrateLayers,
    validateLayerUniqueness
  }
})